import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import "./Css/Settings-responsive.css";
import "./Css/Settings.css";

import { Ripple } from "primereact/ripple";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { Password } from "primereact/password";
import { goToTop } from "../../Components/goToTop";
import { Divider } from "primereact/divider";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { InputOtp } from "primereact/inputotp";
import { userResetPassword } from "../../Api/HeyTraderService/SiteMenuService";
import { checkConfirmPassword, checkPassword } from "../../Utils/Validator";
import { deleteAccountOTPSend, deleteAccountOTPVerify } from "../../Api/SignupApi";
import { ProgressSpinner } from "primereact/progressspinner";


const Settings: React.FC = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");

  /* Confirm password fields */
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  /* Confirm password errors */
  const [currentPasswordError, setCurrentPasswordError] = useState<string>("");
  const [newPasswordError, setNewPasswordError] = useState<string>("");
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>("");

  /* Delete account fields */
  const [showOTPModal, setShowOTPModal] = useState<boolean>(false);
  const [otp, setOTP] = useState<string | number | undefined>();
  const [otpError, setOtpErrorMsg] = useState<string>("");
  const [reSendLoading, setReSendLoading] = useState<boolean>(false);
  const [seconds, setSeconds] = useState<number>(0);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);

  const [deleteAccountLoading, setDeleteAccountLoading] = useState<boolean>(false);
  const [deleteAccountMessage, setDeleteAccountMessage] = useState<string>('');
  const [loadingDeleteAccount, setLoadingDeleteAccount] = useState<boolean>(false);


  /* **************************** Change password ****************************** */
  const header = <div className="font-bold mb-3">Password Strength</div>;
  const footer = (
    <React.Fragment>
      <Divider />
      <p className="mt-2">Suggestions</p>
      <ul className="pl-2 ml-2 mt-0 line-height-3">
        <li>At least one lowercase</li>
        <li>At least one uppercase</li>
        <li>At least one numeric</li>
        <li className="mb-0">Minimum 8 characters</li>
      </ul>
    </React.Fragment>
  );

  const handleClearChangePasswordFields = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleClearChangePasswordErrors = () => {
    setCurrentPasswordError("");
    setNewPasswordError("");
    setConfirmPasswordError("");
  };


  useEffect(() => {
    checkPassword(currentPassword, false, setNewPasswordError);
  }, [currentPassword]);

  useEffect(() => {
    checkPassword(newPassword, false, setNewPasswordError);
  }, [newPassword]);

  useEffect(() => {
    checkConfirmPassword(confirmPassword, newPassword, false, setConfirmPasswordError);
  }, [confirmPassword]);



  function checkCredentials(): boolean {
    const password = checkPassword(newPassword, true, setNewPasswordError);
    const conpassword = checkConfirmPassword(confirmPassword, newPassword, true, setConfirmPasswordError);
    return password && conpassword;
  }


  function showAPIError(keyName: string, error: string): void {

    setConfirmPasswordError('');
    setCurrentPasswordError('');
    setNewPasswordError('');

    if (keyName === "current_password") {

      setCurrentPasswordError(error);
    }
    if (keyName === "new_password") {
      setNewPasswordError(error);
    }
  }

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_type_id');
    localStorage.removeItem('user_type_name');
    localStorage.removeItem('company_name');
    localStorage.removeItem('currency_symbol');
    localStorage.removeItem('material_tax_percentage');
    localStorage.removeItem('vat_percentage');
    navigate('/auth/login');
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (checkCredentials()) {
        setLoading(true);

        const data = await userResetPassword(
          currentPassword,
          newPassword,
          confirmPassword
        );

        setLoading(false);
        if (data.statusCode === 200) {
          // call shop dispach event
          // log out.
          logout();

          setTimeout(() => {
            setLoading(false);
            setSuccessMessage("Password changed successfully.");

            handleClearChangePasswordFields();
            handleClearChangePasswordErrors();

            setShowAlert(true);
            setTimeout(() => {
              setSuccessMessage("");
              setShowAlert(false);
            }, 2000);
          }, 500);
        } else if (data.statusCode === 422) {

          Object.entries(data.errors ?? []).map(([key, value], index) => (
            showAPIError(key, value[0])
          ))

        }
      }
    } catch (error) {
      setLoading(false);
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Failed",
          detail: "There was an error changing the password.",
          life: 3000,
        });
      }
    }
  };
  /* *************************************************************************** */

  /* ***************************** Delete account ****************************** */
  const handleCloseOTPModal = () => {
    confirmDialog({
      message: `Do you want to cancel account deletion?`,
      header: "Confirmation.",
      defaultFocus: "accept",
      acceptClassName: "p-button-primary",
      accept: closeOTPModal,
      dismissableMask: true,
      resizable: false,
      draggable: false,
    });
  };

  const closeOTPModal = () => {
    setShowOTPModal(false);
    setOTP("");
    setOtpErrorMsg("");
    setDeleteAccountLoading(false);
    setDeleteAccountMessage('');
  };

  const otpModalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5">Delete account</h5>
      <button
        type="button"
        className="close_modal_btn p-ripple"
        onClick={handleCloseOTPModal}
      >
        <i className="bi bi-x-circle"></i>
        <Ripple />
      </button>
    </div>
  );

  const handleDeleteAccount = () => {
    confirmDialog({
      message: (
        <div className="text-center d-flex justify-content-center flex-column">
          <span>
            Are you sure you want to delete your account?
            <br />
            <small className="text-center text-secondary">
              The system will remove your data associated with you.
            </small>
          </span>
        </div>
      ),
      header: "Confirmation for account deletion.",
      headerClassName: "confirmation_header_danger",
      defaultFocus: "accept",
      acceptClassName: "p-button-danger",
      accept: handleSendOTP,
      dismissableMask: true,
      resizable: false,
      draggable: false,
    });
  };

  const handleSendOTP = async () => {
    setShowOTPModal(true);
    setOtpErrorMsg('');
    setDeleteAccountLoading(true);
    setDeleteAccountMessage('Your email is being verified, please wait...');

    const data = await deleteAccountOTPSend();

    if (data.status) {
      setDeleteAccountLoading(false);
      setDeleteAccountMessage('');

      if (toast.current) {
        toast.current.show({
          severity: 'success',
          summary: 'Success',
          detail: 'OTP has been sent successfully, check your email inbox.',
          life: 3000
        });
      }

      setSeconds(60);
      setIsButtonDisabled(true);
    } else {
      setDeleteAccountMessage('There was an error verifying your email, please try again later.');
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoadingDeleteAccount(true);

    if (checkOTPInput(Number(otp))) {
      const result = await deleteAccountOTPVerify(String(otp));

      if (result.status) {
        setLoadingDeleteAccount(false);
        closeOTPModal();

        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Your email address has been successfully verified. And your account has been successfully deleted.",
          life: 3000,
        });

        logout();
        goToTop();
      } else {
        setLoadingDeleteAccount(false);

        toast.current?.show({
          severity: "error",
          summary: "Failed",
          detail: result.massage || "Your OTP does not match",
          life: 3000,
        });
      }
    } else {
      setLoadingDeleteAccount(false);

      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Failed",
          detail: "Your OTP does not match",
          life: 3000,
        });
      }
    }
  };

  useEffect(() => {
    if (seconds > 0) {
      const timerId = setTimeout(() => {
        setSeconds(seconds - 1);
      }, 1000);
      return () => clearTimeout(timerId);
    } else {
      setIsButtonDisabled(false);
    }
  }, [seconds]);

  const handleResendCode = async (e: React.FormEvent) => {
    e.preventDefault();

    setReSendLoading(true);
    const data = await deleteAccountOTPSend();

    setSeconds(60);
    setIsButtonDisabled(true);
    setReSendLoading(false);

    if (data.status) {
      if (toast.current) {
        toast.current.show({
          severity: 'success',
          summary: 'Success',
          detail: 'OTP has been sent successfully, check your email inbox.',
          life: 3000
        });
      }
    } else {
      setReSendLoading(false);

      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Email verification failed.",
          detail: "There was an error verifying your email, please try again later.",
          life: 3000,
        });
      }
    }
  };

  const checkOTPInput = (OTP: Number): boolean => {
    if (!OTP) {
      setOtpErrorMsg("OTP field is required.");
      return false;
    } else {
      setOtpErrorMsg("");
      return true;
    }
  };
  /* *************************************************************************** */

  /* **************************** Common functions ***************************** */
  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  const goBack = () => {
    navigate(-1);
    goToTop();
  };
  /* *************************************************************************** */

  return (
    <React.Fragment>
      <Toast ref={toast} />
      <div className="main_content layout_padding show_side_bar">
        <div className="page_content mt-0">
          <div className="d-flex align-items-center justify-content-between">
            <h4 className="main_heading">Settings</h4>
            <button className="back_btn p-ripple" onClick={goBack}>
              <i className="bi bi-arrow-left me-2"></i>
              Back
              <Ripple />
            </button>
          </div>

          <div className="page_main_content_section p-sm-4 p-3">
            <div className="row">
              <div className="col-12 col-xl-6 col-sm-10 mx-auto">
                <h6 className="change_password_head text-center mb-0">
                  Change password
                </h6>
                <div className="change_password_image_area">
                  <img
                    src="/assets/images/settings/change_password.svg"
                    alt="Chnage password"
                  />
                </div>
              </div>

              <div className="col-12 col-sm-8 mx-auto col-xl-6">
                <div className="change_password_container">
                  <div className="change_password_form">
                    <div className="row">
                      <div className="col-12">
                        <div className="custom_form_group mb-4">
                          <label
                            htmlFor="currentPassword"
                            className="custom_form_label is_required"
                          >
                            Current password
                          </label>
                          <Password
                            key={`currentPassword`}
                            value={currentPassword}
                            inputId="currentPassword"
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => setCurrentPassword(e.target.value)}
                            feedback={false}
                            toggleMask
                            tabIndex={1}
                            className="custom_form_input w-100"
                            placeholder="Enter your current password"
                          />
                          {currentPasswordError && (
                            <small className="form_error_msg">
                              {currentPasswordError}
                            </small>
                          )}
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="custom_form_group mb-4">
                          <label
                            htmlFor="newPassword"
                            className="custom_form_label is_required"
                          >
                            New password
                          </label>
                          <Password
                            key={`newPassword`}
                            value={newPassword}
                            inputId="newPassword"
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => setNewPassword(e.target.value)}
                            header={header}
                            footer={footer}
                            toggleMask
                            tabIndex={1}
                            className="custom_form_input w-100"
                            placeholder="Enter a new password"
                          />
                          {newPasswordError && (
                            <small className="form_error_msg">
                              {newPasswordError}
                            </small>
                          )}
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="custom_form_group mb-4">
                          <label
                            htmlFor="confirmPasswrord"
                            className="custom_form_label is_required"
                          >
                            Confirm password
                          </label>
                          <Password
                            value={confirmPassword}
                            inputId="confirmPasswrord"
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => setConfirmPassword(e.target.value)}
                            feedback={false}
                            toggleMask
                            tabIndex={1}
                            className="custom_form_input w-100"
                            placeholder="Confirm password"
                          />
                          {confirmPasswordError && (
                            <small className="form_error_msg">
                              {confirmPasswordError}
                            </small>
                          )}
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="custom_form_btn_area mt-1">
                          <Button
                            label={`${loading ? "Processing" : "Save"}`}
                            className="custom_form_btn letter_spaced w-100"
                            loading={loading}
                            onClick={handleChangePassword}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Divider />

            <div className="row">
              <div className="col-12">
                <div className="delete_account_area">
                  <Button
                    label={`Delete account`}
                    icon={`bi bi-trash3`}
                    onClick={handleDeleteAccount}
                    className="custom_btn"
                    severity="danger"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        visible={showOTPModal}
        headerClassName="custom_modal_header"
        header={otpModalHeader}
        draggable={false}
        closable={false}
        dismissableMask
        closeOnEscape={false}
        className="custom_modal_dialog modal_dialog_sm"
        onHide={handleCloseOTPModal}
      >
        <div className="custom_modal_body">
          <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
            {deleteAccountLoading ? (
              <div className="feature_not_available_area">
                <h6>{deleteAccountMessage}</h6>
              </div>
            ) : (
              <div className="row">
                <div className="col-12">
                  <div className="custom_form_group">
                    <label
                      htmlFor="email"
                      className="custom_form_label text-center mb-4"
                    >
                      Enter the OTP below
                    </label>

                    <InputOtp
                      id="otp"
                      value={otp}
                      onChange={(e) => setOTP(e.value ?? undefined)}
                      integerOnly
                    />
                    {otpError && (
                      <small className="form_error_msg text-center mt-4">
                        {otpError}
                      </small>
                    )}
                  </div>
                </div>

                <div className="col-12">
                  <div className="custom_form_btn_area">
                    <Button
                      label={`${loadingDeleteAccount
                        ? "Verifying"
                        : "VERIFY"
                        }`}
                      className="custom_form_btn letter_spaced w-100"
                      onClick={handleVerifyOTP}
                      loading={loadingDeleteAccount}
                      disabled={!otp || String(otp)?.length < 4}
                    />
                  </div>
                </div>

                <div className="col-12">
                  <div className="custom_form_bottom_area mb-0">
                    <p className="mb-0 d-flex align-items-center justify-content-center gap-2">
                      Didn’t received the code?
                      {reSendLoading ? (
                        <React.Fragment>
                          <ProgressSpinner
                            className="progresscircle custom m-0"
                            style={{ width: '20px', height: '20px', stroke: '#8953D7' }}
                            strokeWidth="8"
                            fill="#FFF"
                            animationDuration="1s"
                          />

                          <span className="text_secondary">
                            Sending...
                          </span>
                        </React.Fragment>
                      ) : (
                        <button
                          onClick={handleResendCode}
                          disabled={isButtonDisabled}
                        >
                          <span>
                            Resend Code {isButtonDisabled && `in (${seconds}s)`}
                          </span>
                        </button>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Dialog>

      {/* Show success message */}
      <div
        className={`message_backdrop ${showAlert ? "show" : ""}`}
        onClick={handleCloseAlert}
      >
        <div
          className={`message_box ${showAlert ? "show" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="message_box_content">
            <i className="bi bi-check-circle-fill message_box_icon"></i>
            <h6 className="message_box_message">{successMessage}</h6>
          </div>
        </div>
      </div>
      {/*  */}
    </React.Fragment>
  );
};

export default Settings;
